/**
 * One money representation for the whole console (V2.2-01).
 *
 * THE DEFECT THIS REPLACES. A single sale used to be described by three
 * disagreeing rules: `formatRs` floored every amount and hid decimals, the
 * "Exact" tender suggestion used `Math.ceil`, and change was subtracted from
 * the unrounded decimal and then floored again for display. The visible result
 * was a receipt reading total Rs 6,089, cash received Rs 6,090, change Rs 0 —
 * three numbers that cannot all be true. That is a money bug, not a formatting
 * preference.
 *
 * THE RULE. Amounts are carried as integer MINOR units (paisa) and only ever
 * become a string at the edge. Integers make subtraction exact, so displayed
 * total, Exact, cash received, recorded cash and change agree by construction
 * rather than by luck.
 *
 * The server remains authoritative. This module parses its decimal strings and
 * formats them back; it never decides a price.
 */

/** Integer paisa. Named so a plain rupee number cannot be passed by accident. */
export type Minor = number;

const MINOR_PER_UNIT = 100;

/**
 * Parse a server decimal string ("6089.25") into paisa.
 *
 * Rounds at the last step because `6089.25 * 100` is 6089.249999999999 in IEEE
 *754 — truncating that would lose a paisa on perfectly ordinary totals.
 * Anything unparseable is 0 rather than NaN: a NaN propagates silently through
 * every subsequent sum and surfaces as a blank amount on a receipt.
 */
export function parseMinor(value: string | number | null | undefined): Minor {
  if (value == null) return 0;
  const parsed = typeof value === 'number' ? value : Number.parseFloat(value);
  if (!Number.isFinite(parsed)) return 0;
  return Math.round(parsed * MINOR_PER_UNIT);
}

/** Rupees (possibly fractional) → paisa. */
export function toMinor(rupees: number): Minor {
  if (!Number.isFinite(rupees)) return 0;
  return Math.round(rupees * MINOR_PER_UNIT);
}

/**
 * Paisa → a rupee number.
 *
 * For sending to an API or feeding a numeric input ONLY. Never use it as an
 * intermediate in arithmetic — that reintroduces exactly the float error the
 * minor units exist to avoid.
 */
export function toRupees(minor: Minor): number {
  return minor / MINOR_PER_UNIT;
}

/** Decimal string an API expects: always two places, never grouped. */
export function toDecimalString(minor: Minor): string {
  const negative = minor < 0;
  const abs = Math.abs(Math.round(minor));
  const whole = Math.floor(abs / MINOR_PER_UNIT);
  const fraction = abs % MINOR_PER_UNIT;
  return `${negative ? '-' : ''}${whole}.${String(fraction).padStart(2, '0')}`;
}

/**
 * Display form: `Rs 6,089` when whole, `Rs 6,089.25` when not.
 *
 * Trailing `.00` is dropped because most restaurant totals are whole and two
 * dead zeroes on every line makes a ticket harder to scan. A real fraction is
 * NEVER hidden — hiding it is what let the old formatter show 6,089 for a
 * 6,089.25 sale.
 */
export function formatMinor(minor: Minor): string {
  const negative = minor < 0;
  const abs = Math.abs(Math.round(minor));
  const whole = Math.floor(abs / MINOR_PER_UNIT);
  const fraction = abs % MINOR_PER_UNIT;
  const grouped = whole.toLocaleString('en-US');
  const body = fraction === 0 ? grouped : `${grouped}.${String(fraction).padStart(2, '0')}`;
  return `Rs ${negative ? '-' : ''}${body}`;
}

/** Change owed, never negative — short cash is refused before it gets here. */
export function changeMinor(receivedMinor: Minor, totalMinor: Minor): Minor {
  return Math.max(0, receivedMinor - totalMinor);
}

/**
 * Cash suggestions for the keypad.
 *
 * The FIRST entry is the exact total — not `Math.ceil(total)`, which is what
 * made "Exact" tender Rs 6,090 against a Rs 6,089.25 bill and then report Rs 0
 * change. Round notes above it are separate, clearly-labelled suggestions; they
 * are convenience, and Exact is correctness.
 */
export function tenderSuggestionsMinor(totalMinor: Minor): Minor[] {
  if (!Number.isFinite(totalMinor) || totalMinor <= 0) return [];
  const notes = [500, 1000, 5000].map((step) => {
    const stepMinor = step * MINOR_PER_UNIT;
    return Math.ceil(totalMinor / stepMinor) * stepMinor;
  });
  return [...new Set([totalMinor, ...notes])].filter((n) => n >= totalMinor).slice(0, 4);
}
